import { type PropsWithChildren, type ReactNode } from 'react';

import { type RequestContext } from '~/types/RequestContext';

import { type BettingInsights } from '../BettingInsightsConfig';
import { BettingInsightsComponent } from '../BettingInsightsConfiguration';
import {
	type BettingInsightsContent,
	type BettingInsightsResult,
	type EsIndex,
} from '../types';

import {
	BettingInsightsTable,
	type BettingInsightsTableConfig,
} from './BettingInsightsTable';
import {
	BettingInsightsTitle,
	type BettingInsightsTitleConfig,
} from './BettingInsightsTitle';

export type WrapperParams<TIndex extends EsIndex> = {
	config: BettingInsights<TIndex>;
	content: BettingInsightsContent<TIndex>[];
	resourcesUrl?: string;
	result: BettingInsightsResult<TIndex>;
	resultIndex?: number;
} & PropsWithChildren;

export interface BettingInsightsCardConfig<TIndex extends EsIndex> {
	styles: { readonly [key: string]: string };
	table: BettingInsightsTableConfig<TIndex>;
	title: BettingInsightsTitleConfig<TIndex>;
	wrapper: ({
		children,
		config,
		content,
		resourcesUrl,
		result,
		resultIndex,
	}: WrapperParams<TIndex>) => ReactNode;
}

export class BettingInsightsCard<TIndex extends EsIndex>
	extends BettingInsightsComponent
	implements BettingInsightsCardConfig<TIndex>
{
	styles: { readonly [key: string]: string };
	table: BettingInsightsTable<TIndex>;
	title: BettingInsightsTitle<TIndex>;
	wrapper: ({
		children,
		config,
		content,
		resourcesUrl,
		result,
		resultIndex,
	}: WrapperParams<TIndex>) => ReactNode;

	constructor({
		styles,
		table,
		title,
		wrapper,
		...requestContext
	}: BettingInsightsCardConfig<TIndex> & RequestContext) {
		super(requestContext);
		this.styles = styles;
		this.table = BettingInsightsTable.withRequestContext<TIndex>(
			requestContext,
			table,
		);
		this.title = BettingInsightsTitle.withRequestContext<TIndex>(
			requestContext,
			title,
		);
		this.wrapper = wrapper.bind(this);
	}

	static withRequestContext<TIndex extends EsIndex>(
		requestContext: RequestContext,
		config: BettingInsightsCardConfig<TIndex>,
	) {
		return new BettingInsightsCard<TIndex>({
			...requestContext,
			...config,
		});
	}
}
